import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n/config';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { Layout } from './components/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';

// Pages
import { Login } from './pages/Login';
import { Profile } from './pages/Profile';
import { TrainingDetail } from './pages/user/TrainingDetail';
import { UserCalendar } from './pages/user/UserCalendar';
import { MyTrainings } from './pages/user/MyTrainings';
import { RunTraining } from './pages/user/RunTraining';
import { CoachTrainings } from './pages/coach/CoachTrainings';
import { CoachCalendar } from './pages/coach/CoachCalendar';
import { CoachExercises } from './pages/coach/CoachExercises';
import { CoachCategories } from './pages/coach/CoachCategories';
import { CoachTrainees } from './pages/coach/CoachTrainees';
import { CreateTraining } from './pages/coach/CreateTraining';
import { AdminUsers } from './pages/admin/AdminUsers';

const RootRedirect: React.FC = () => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <div className="flex justify-center items-center" style={{ minHeight: '100vh' }}>Cargando...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role === 'user') return <Navigate to="/calendar" replace />;
  if (user.role === 'coach') return <Navigate to="/coach/trainings" replace />;
  if (user.role === 'admin') return <Navigate to="/admin/users" replace />;

  return <Navigate to="/login" replace />;
};

function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <Routes>
            <Route path="/login" element={<Login />} />

            <Route
              path="/"
              element={<RootRedirect />}
            />

            {/* User routes */}
            <Route
              path="/calendar"
              element={
                <ProtectedRoute allowedRoles={['user']}>
                  <Layout>
                    <UserCalendar />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/my-trainings"
              element={
                <ProtectedRoute allowedRoles={['user']}>
                  <Layout>
                    <MyTrainings />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/user/training/run/:id"
              element={
                <ProtectedRoute allowedRoles={['user']}>
                  <Layout>
                    <RunTraining />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Coach routes */}
            <Route
              path="/coach/calendar"
              element={
                <ProtectedRoute allowedRoles={['coach']}>
                  <Layout>
                    <CoachCalendar />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/coach/trainings"
              element={
                <ProtectedRoute allowedRoles={['coach']}>
                  <Layout>
                    <CoachTrainings />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/coach/trainings/create"
              element={
                <ProtectedRoute allowedRoles={['coach']}>
                  <Layout>
                    <CreateTraining />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/coach/exercises"
              element={
                <ProtectedRoute allowedRoles={['coach']}>
                  <Layout>
                    <CoachExercises />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/coach/categories"
              element={
                <ProtectedRoute allowedRoles={['coach']}>
                  <Layout>
                    <CoachCategories />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/coach/trainees"
              element={
                <ProtectedRoute allowedRoles={['coach']}>
                  <Layout>
                    <CoachTrainees />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Admin routes */}
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Layout>
                    <AdminUsers />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/trainings"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Layout>
                    <CoachTrainings />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Shared routes */}
            <Route
              path="/training/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TrainingDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Profile />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* 404 */}
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </I18nextProvider>
  );
}

export default App;
